import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { cdnUrl } from "commons/cdn";
import { formatTime } from "commons";
import Loader from "@/components/ui/Loader";
import EmptyDatas from "components/ui/EmptyDatas";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useToast } from "@/components/ui/use-toast";
import { getFetcher, putFetcher } from "api/swr";
import RecreateStatsChart from "./recreate-stats-chart";
import { MyImage } from "components/MyImage";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CircleHelp,
  Heart,
  Star,
  Loader2,
  Paintbrush,
  Percent,
  RefreshCw,
  Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const toDatetimeLocalValue = (ts?: number) => {
  if (!ts) return "";
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const queryDateFromLocal = (local: string) => {
  const m = local.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : undefined;
};

const formatRate = (v: any) => {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return "—";
  return `${v}%`;
};

const formatCount = (v: any) => {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString();
};

/** 去掉以 # 开头的标签行，只保留正常简介文案 */
const cleanIntro = (intro?: string) => {
  if (!intro) return "";
  return String(intro)
    .split(/\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"))
    .join(" ")
    .trim();
};

const TimeText = ({ ts }: { ts?: number }) => {
  if (!ts) return <>{"—"}</>;
  const [showTime, ago] = formatTime(ts);
  if (!showTime) return <>{"—"}</>;
  return (
    <>
      {showTime}
      <span className="ml-1 opacity-70">({ago})</span>
    </>
  );
};

const StatHint = ({ text }: { text: string }) => (
  <HoverCard openDelay={120} closeDelay={80}>
    <HoverCardTrigger asChild>
      <button
        type="button"
        className="inline-flex text-slate-400 transition-colors hover:text-slate-600"
        aria-label="说明"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent
      align="start"
      className="w-72 p-3 text-xs leading-5 text-slate-600"
    >
      {text}
    </HoverCardContent>
  </HoverCard>
);

const StatCard = ({
  label,
  value,
  icon: Icon,
  hint,
  labelAction,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
  labelAction?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-[#F6F7F9] px-4 py-3.5">
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500">
        <span>{label}</span>
        {hint ? <StatHint text={hint} /> : null}
        {labelAction}
      </div>
      <div className="mt-1.5 text-[22px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-900">
      <Icon className="h-4 w-4" />
    </div>
  </div>
);

const RefreshMetricButton = ({
  tid,
  label,
  fetchPath,
  updateKey,
  currentValue,
  date,
  onUpdated,
}: {
  tid: string;
  label: string;
  fetchPath: string;
  updateKey: string;
  currentValue?: number | null;
  date?: string;
  onUpdated: () => void;
}) => {
  const { toast } = useToast();
  const { trigger: fetchTrigger, isMutating: isFetching } = useSWRMutation(
    `/frames/${tid}/${fetchPath}`,
    async (url, { arg }: { arg?: { date?: string } }) =>
      getFetcher(url, arg?.date ? { date: arg.date } : {}),
  );
  const { trigger: updateTrigger, isMutating: isUpdating } = useSWRMutation(
    `/frames/${tid}`,
    putFetcher,
  );
  const isMutating = isFetching || isUpdating;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const fetched = await fetchTrigger(date ? { date } : undefined);
      if (fetched === null || fetched === undefined) {
        toast({
          title: "Info",
          description: `未查询到 ${label} 数据`,
        });
        return;
      }

      if (currentValue !== fetched) {
        await updateTrigger({ key: updateKey, value: fetched });
        toast({
          title: "Success",
          description: `${label}已更新: ${currentValue ?? "-"}% → ${fetched}%`,
          className: "bg-green-500 text-white",
        });
        onUpdated();
      } else {
        toast({
          title: "Info",
          description: `${label}无变化: ${fetched}%`,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${label}获取失败: ` + (error.message || "Unknown Error"),
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-5 w-5 text-slate-400 hover:text-slate-700"
      disabled={isMutating}
      onClick={handleClick}
      aria-label={`刷新 ${label}`}
      title={`刷新 ${label}`}
    >
      {isMutating ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
    </Button>
  );
};

const UnpublishedNotice = ({ tid }: { tid: string }) => (
  <div className="flex w-full min-w-0 flex-col gap-4">
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
      <p className="text-base font-medium text-slate-800">
        该模版尚未上线过，暂无报表数据
      </p>
      <p className="mt-2 text-sm text-slate-500">
        未检测到 publishTime（tid: {tid}）。上线后即可查看使用率与统计。
      </p>
    </div>
  </div>
);

const CARD_BG = "#F6F7F9";

/** 美区 New 安卓漏斗三率卡片（对齐 recreate 曲线图卡片样式） */
const UsNewAndroidRatesCard = ({
  tid,
  live,
  date,
  onUpdated,
}: {
  tid: string;
  live: {
    d1UsNewClickRate?: number | null;
    d1UsNewUsageRate?: number | null;
    d1UsNewDoneRate?: number | null;
  };
  date?: string;
  onUpdated: () => void;
}) => {
  const { toast } = useToast();
  const { trigger: fetchTrigger, isMutating: isFetching } = useSWRMutation(
    `/frames/${tid}/d1-us-new-android-rates`,
    async (url, { arg }: { arg?: { date?: string } }) =>
      getFetcher(url, arg?.date ? { date: arg.date } : {}),
  );
  const { trigger: updateTrigger, isMutating: isUpdating } = useSWRMutation(
    `/frames/${tid}`,
    putFetcher,
  );
  const isMutating = isFetching || isUpdating;

  const handleRefresh = async () => {
    try {
      const fetched = await fetchTrigger(date ? { date } : undefined);
      if (!fetched) {
        toast({
          title: "Info",
          description: "未查询到美区 New 安卓漏斗率数据",
        });
        return;
      }

      const pairs: Array<{
        key: "d1UsNewClickRate" | "d1UsNewUsageRate" | "d1UsNewDoneRate";
        label: string;
        next: number;
        prev?: number | null;
      }> = [
        {
          key: "d1UsNewClickRate",
          label: "点击率",
          next: fetched.d1UsNewClickRate,
          prev: live.d1UsNewClickRate,
        },
        {
          key: "d1UsNewUsageRate",
          label: "使用率",
          next: fetched.d1UsNewUsageRate,
          prev: live.d1UsNewUsageRate,
        },
        {
          key: "d1UsNewDoneRate",
          label: "完成率",
          next: fetched.d1UsNewDoneRate,
          prev: live.d1UsNewDoneRate,
        },
      ];

      let changed = 0;
      for (const p of pairs) {
        if (p.next == null || Number.isNaN(Number(p.next))) continue;
        if (p.prev !== p.next) {
          await updateTrigger({ key: p.key, value: p.next });
          changed += 1;
        }
      }

      if (changed > 0) {
        toast({
          title: "Success",
          description: `已更新 ${changed} 项美区 New 安卓漏斗率`,
          className: "bg-green-500 text-white",
        });
        onUpdated();
      } else {
        toast({
          title: "Info",
          description: "美区 New 安卓漏斗率无变化",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          "美区 New 安卓漏斗率获取失败: " + (error.message || "Unknown Error"),
        variant: "destructive",
      });
    }
  };

  const rows = [
    {
      label: "点击率",
      value: live.d1UsNewClickRate,
      hint: "美区安卓 New 列表：Click UV / Show UV（查率日期当日）",
    },
    {
      label: "使用率",
      value: live.d1UsNewUsageRate,
      hint: "美区安卓 New 列表：Editor_Open UV / Show UV（查率日期当日）",
    },
    {
      label: "完成率",
      value: live.d1UsNewDoneRate,
      hint: "美区安卓 New 列表：Done UV / Show UV（查率日期当日）",
    },
  ];

  return (
    <div
      className="w-full max-w-[520px] min-w-0 overflow-hidden rounded-xl border border-slate-200"
      style={{ backgroundColor: CARD_BG }}
    >
      <div className="flex items-center justify-between px-3 pb-3 pt-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <h3 className="text-base font-semibold tracking-tight text-slate-900">
            New D1 漏斗率（美区安卓）
          </h3>
          <StatHint text="按上方查率日期，美区安卓 New 分栏：曝光 / 点击 / 进编辑器 / 完成 的 UV 漏斗比率。上线后约 26h 由 worker 自动回填。" />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 text-slate-400 hover:text-slate-700"
          disabled={isMutating}
          onClick={handleRefresh}
          aria-label="刷新 New 漏斗率"
          title="重新获取"
        >
          {isMutating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      <div className="px-1 pb-1">
        <div className="rounded-xl border border-slate-200 bg-white px-2 pb-2 pt-3">
          <div className="flex aspect-[16/9] w-full flex-col justify-evenly gap-2">
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex flex-1 items-center justify-between gap-3 rounded-lg bg-[#F6F7F9] px-3"
              >
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500">
                  <span>{row.label}</span>
                  <StatHint text={row.hint} />
                </div>
                <div className="text-[22px] font-semibold tabular-nums leading-none text-slate-900">
                  {formatRate(row.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportSummary = () => {
  const { id: tid } = useParams<{ id: string }>();
  const [shelfLocal, setShelfLocal] = useState("");
  const {
    data: template,
    isLoading: templateLoading,
    error: templateError,
  } = useSWR(tid ? [`/templates/${tid}`] : null);

  const hasPublished = Boolean(template?.publishTime);
  const {
    data: live,
    isLoading: liveLoading,
    mutate: mutateLive,
  } = useSWR(
    tid && hasPublished ? [`/frames/${tid}/live-summary`] : null,
  );
  const { data, isLoading, isValidating } = useSWR(
    tid && hasPublished ? [`/frames/daily`, { tid }] : null,
  );

  if (!tid || templateLoading) {
    return <Loader />;
  }
  if (templateError || !template) {
    return <EmptyDatas />;
  }
  if (!hasPublished) {
    return <UnpublishedNotice tid={tid} />;
  }
  if (((isLoading || isValidating) && !data) || (liveLoading && !live)) {
    return <Loader />;
  }
  if (!live) return <EmptyDatas />;

  const previewSrc = live.preview || live.cover;
  const intro = cleanIntro(live.intro);
  const defaultLocal = toDatetimeLocalValue(live.createTime);
  const localValue = shelfLocal || defaultLocal;
  const queryDate = queryDateFromLocal(localValue);
  const isShelfOverridden = Boolean(shelfLocal && shelfLocal !== defaultLocal);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          <div className="mx-auto w-full max-w-[320px] shrink-0 lg:mx-0">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-black">
              {previewSrc ? (
                <MyImage
                  className="aspect-square w-full object-contain"
                  src={cdnUrl(previewSrc)}
                  alt={live.name || live.id}
                  lazy={false}
                  preview
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center text-sm text-slate-400">
                  No preview
                </div>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                {live.name || live.id}
              </h2>
              <p className="mt-2 line-clamp-2 max-w-3xl text-sm leading-relaxed text-slate-500">
                {intro || "暂无简介"}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                  <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                  Create <TimeText ts={live.createTime} />
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                  <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                  Publish <TimeText ts={live.publishTime} />
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                  <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                  查率日期
                  <StatHint text="刷新各率时按此日期查询（D1=当天，D2=次日，D3=第3日）。默认 createTime，只在本页生效，不写入模版。" />
                  <Input
                    type="datetime-local"
                    value={localValue}
                    onChange={(e) => setShelfLocal(e.target.value)}
                    className="h-7 w-[16rem] border-slate-200 bg-white px-1.5 py-0 text-xs shadow-none"
                  />
                  {isShelfOverridden ? (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-slate-500 hover:text-slate-800"
                      onClick={() => setShelfLocal("")}
                    >
                      重置
                    </button>
                  ) : null}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
              <StatCard
                label="Avatars"
                value={formatCount(live.avatarCount)}
                icon={Users}
              />
              <StatCard
                label="reCreate / daily"
                value={
                  <>
                    {formatCount(live.reCreateCount)}
                    <span className="text-base font-medium text-slate-400">
                      {" "}
                      / {formatCount(live.dailyCount?.reCreateCount)}
                    </span>
                  </>
                }
                icon={Paintbrush}
              />
              <StatCard
                label="Like"
                value={formatCount(live.likeCount)}
                icon={Heart}
              />
              <StatCard
                label="Star"
                value={formatCount(live.starCount)}
                icon={Star}
                hint="收藏数（Redis 实时）"
              />
              <StatCard
                label="Flags"
                value={formatCount(live.reportCount)}
                icon={AlertTriangle}
                hint="举报次数"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
              <StatCard
                label="D1 Usage"
                value={formatRate(live.d1UsageRate)}
                icon={Percent}
                hint="按上方查率日期当日计算：该模版 Editor_Open PV / 当日全站 Editor_Open PV"
                labelAction={
                  <RefreshMetricButton
                    tid={tid}
                    label="D1 使用率"
                    fetchPath="d1-usage-rate"
                    updateKey="d1UsageRate"
                    currentValue={live.d1UsageRate}
                    date={queryDate}
                    onUpdated={() => mutateLive()}
                  />
                }
              />
              <StatCard
                label="D2 Usage"
                value={formatRate(live.d2UsageRate)}
                icon={Percent}
                hint="按上方查率日期次日计算：该模版 Editor_Open PV / 当日全站 Editor_Open PV"
                labelAction={
                  <RefreshMetricButton
                    tid={tid}
                    label="D2 使用率"
                    fetchPath="d2-usage-rate"
                    updateKey="d2UsageRate"
                    currentValue={live.d2UsageRate}
                    date={queryDate}
                    onUpdated={() => mutateLive()}
                  />
                }
              />
              <StatCard
                label="D3 Usage"
                value={formatRate(live.d3UsageRate)}
                icon={Percent}
                hint="按上方查率日期第 3 日计算：该模版 Editor_Open PV / 当日全站 Editor_Open PV"
                labelAction={
                  <RefreshMetricButton
                    tid={tid}
                    label="D3 使用率"
                    fetchPath="d3-usage-rate"
                    updateKey="d3UsageRate"
                    currentValue={live.d3UsageRate}
                    date={queryDate}
                    onUpdated={() => mutateLive()}
                  />
                }
              />
              <StatCard
                label="D1 完成率"
                value={formatRate(live.d1DoneRate)}
                icon={CheckCircle2}
                hint="按上方查率日期当日计算：该模版 Editor_Done_Click PV / Editor_Open PV"
                labelAction={
                  <RefreshMetricButton
                    tid={tid}
                    label="D1 完成率"
                    fetchPath="d1-done-rate"
                    updateKey="d1DoneRate"
                    currentValue={live.d1DoneRate}
                    date={queryDate}
                    onUpdated={() => mutateLive()}
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <RecreateStatsChart data={data?.reCreateCount} />
        <UsNewAndroidRatesCard
          tid={tid}
          live={live}
          date={queryDate}
          onUpdated={() => mutateLive()}
        />
      </div>
    </div>
  );
};

export default ReportSummary;
